<template>
  <ValidationForm ref="formValidator">
    <Modal v-model="showModal"
           :static="true"
           :title="tr('chartStatic.SETTINGS_TITLE')"
           :is-loading="isLoading"
           @hide="removeFromDOM">
      <BFormGroup v-if="isInControlRoom === true"
                  :label="tr('chartStatic.DISPLAY_MODE')"
                  :label-cols="3"
                  label-class="pt-0"
                  label-for="system-selection-chart-static">
        <BFormRadio v-model="systemType"
                    :value="SystemSelectionType.DYNAMIC"
                    @change="onChange">
          {{ tr('chartStatic.DYNAMIC') }}
          <InfoIconWithTooltip :title="tr('chartStatic.DISPLAY_MODE_DYNAMIC_INFO')"></InfoIconWithTooltip>
        </BFormRadio>
        <div class="custom-control custom-radio">
          <input id="systems-type-radio"
                 v-model="systemType"
                 class="custom-control-input"
                 type="radio"
                 :value="SystemSelectionType.SYSTEM"
                 @change="onChange">
          <label class="custom-control-label"
                 for="systems-type-radio">
            {{ tr('chartStatic.SELECTED_SYSTEM') }}
          </label>
          <BCollapse :visible="systemType === SystemSelectionType.SYSTEM"
                     class="pt-1"
                     accordion="accordion-1">
            <Field v-slot="{ componentField, meta, errorMessage }"
                   v-model="selectedSystem"
                   :rules="SystemSelectionValidator"
                   :name="tr('chartStatic.SELECTED_SYSTEM')">
              <Selection id="system-selection-chart-static"
                         :model-value="componentField.modelValue"
                         label="label"
                         :placeholder="tr('javascript.TX_NO_SYSTEM_SELECTED')"
                         :options="systemOptions"
                         :searchable="true"
                         @input="onChange"
                         @select="componentField.onInput($event)">
              </Selection>
              <BFormInvalidFeedback :state="getStateForComponentValidation(meta)">
                {{ errorMessage }}
              </BFormInvalidFeedback>
            </Field>
          </BCollapse>
        </div>
      </BFormGroup>

      <BFormGroup :label="tr('chartStatic.CHART')"
                  :label-cols="3"
                  label-for="chart-selection-chart-static-settings">
        <Selection id="chart-selection-chart-static-settings"
                   v-model="selectedChart"
                   label="label"
                   :loading="isLoadingCharts"
                   :disabled="isLoadingCharts"
                   group-values="charts"
                   group-label="section"
                   :searchable="true"
                   :options="chartOptions"
                   @input="preparePeriodSelection">
        </Selection>
      </BFormGroup>

      <BFormGroup :label="tr('portal.TX_PERIOD')"
                  :label-cols="3"
                  label-for="period-selection-chart-static-settings">
        <Selection id="period-selection-chart-static-settings"
                   v-model="selectedPeriod"
                   :disabled="isPeriodSelectionDisabled || isLoadingCharts"
                   :loading="isLoadingCharts"
                   label="label"
                   :options="periodOptions">
        </Selection>
      </BFormGroup>

      <template #modal-footer="{ hide }">
        <SaveModalButtonGroup @save="saveConfig"
                              @cancel="hide">
        </SaveModalButtonGroup>
      </template>
    </Modal>
  </ValidationForm>
</template>

<script>
  import {
    BFormGroup, BCollapse, BFormInvalidFeedback, BFormRadio,
  } from 'bootstrap-vue';
  import { Form as ValidationForm, Field } from 'vee-validate/compat';
  import flatMap from 'lodash/flatMap';
  import { translator } from '~/zf/frontend/common/mixins';
  import validation from '../../common/mixins/validation';
  import Validator from '../../common/veeValidate/Validator';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';
  import Selection from '../../common/selection/Selection.vue';
  import SaveModalButtonGroup from '../../common/layout/SaveModalButtonGroup.vue';
  import InfoIconWithTooltip from '../../common/layout/InfoIconWithTooltip.vue';
  import SystemSelectionType from './const/SystemSelectionType';
  import Base from '~/zf/frontend/vcom/Base';
  import VcomSection from '~/zf/frontend/vcom/const/VcomSection';
  import VcomProvider from '../../VcomProvider';
  import ChartStaticService from './ChartStaticService';
  import Modal from '../../common/layout/Modal.vue';
  import AlertService from '~/zf/frontend/common/alert/AlertService';
  import HttpStatusCode from '~/zf/frontend/common/http/const/HttpStatusCode';
  import EventService from '~/zf/frontend/common/event/EventService';

  const periodLabelMapping = {
    tag: 'javascript.TX_DAY',
    woche: 'javascript.TX_WEEK',
    monat: 'javascript.TX_MONTH',
    jahr: 'javascript.TX_YEAR',
    gesamt: 'javascript.TX_TOTAL',
  };

  export default {
    name: 'ChartStaticSettings',
    components: {
      InfoIconWithTooltip,
      SaveModalButtonGroup,
      Selection,
      Modal,
      ValidationForm,
      Field,
      BFormGroup,
      BCollapse,
      BFormInvalidFeedback,
      BFormRadio,
    },
    mixins: [
      translator,
      validation,
    ],
    props: {
      portletUId: {
        type: Number,
        required: true,
      },
      initialSystemId: {
        type: [String, Number],
      },
      initialChartKey: {
        type: String,
      },
      initialPeriod: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        BootstrapVariant,
        chartOptions: [],
        isLoading: false,
        isLoadingCharts: false,
        isPeriodSelectionDisabled: false,
        selectedChart: null,
        selectedSystem: null,
        selectedPeriod: null,
        systemOptions: [],
        systemType: null,
        SystemSelectionType,
        periodOptions: [],
        showModal: false,
      };
    },
    computed: {
      SystemSelectionValidator() {
        return this.systemType === SystemSelectionType.SYSTEM ? Validator.REQUIRED : null;
      },
      isInControlRoom() {
        return VcomProvider.section === VcomSection.CONTROL_ROOM;
      },
      isOnSystemLevel() {
        return !this.isInControlRoom;
      },
    },
    async mounted() {
      this.isLoading = true;
      this.showModal = true;
      await this.updateSystemOptionsAccordingToSection();
      await this.prepareChartSelection();
      this.preselectChart();

      this.preparePeriodSelection();
      this.preselectPeriod();
      this.isLoading = false;
    },
    methods: {
      async saveConfig() {
        const validationResult = await this.$refs.formValidator.validate();
        if (validationResult.valid === false) {
          return;
        }

        this.isLoading = true;
        const selectedSystem = this.systemType === SystemSelectionType.DYNAMIC
          ? SystemSelectionType.DYNAMIC
          : this.selectedSystem.value;

        try {
          await ChartStaticService.saveChartConfiguration(
            this.portletUId,
            selectedSystem,
            this.selectedChart.key,
            this.selectedPeriod.value,
          );
        } catch (exception) {
          // eslint-disable-next-line fp/no-throw
          if (exception.response.status !== HttpStatusCode.HTTP_NOT_FOUND) throw exception;
          jQuery(`#portlet_${this.portletUId}`).portlet('close', null, true);
          AlertService.showInfo(this.tr('portlet.DELETED_SYSTEM_DIAGRAM_IN_ANOTHER_SESSION'));
          return;
        }

        this.isLoading = false;
        this.showModal = false;
        EventService.dispatch('on-config-saved', {
          target: document.getElementById(`vue-portlet_${this.portletUId}`),
          data: {
            selectedSystem,
            chartKey: this.selectedChart.key,
            selectedPeriod: this.selectedPeriod.value,
            availablePeriods: this.periodOptions.map((periodOption) => periodOption.value),
          },
        });
      },
      async updateSystemOptionsAccordingToSection() {
        this.systemType = this.initialSystemId === SystemSelectionType.DYNAMIC
          ? SystemSelectionType.DYNAMIC
          : SystemSelectionType.SYSTEM;

        if (this.isInControlRoom) {
          await this.updateSystemOptions();
          this.preselectSystemOption();
        }

        if (this.isOnSystemLevel) {
          this.selectedSystem = { value: SystemSelectionType.DYNAMIC };
        }
      },
      removeFromDOM() {
        EventService.dispatch('remove-from-dom', { target: document.getElementById(`vue-portlet_${this.portletUId}`) });
      },
      preselectSystemOption() {
        const initialSystemOption = this.systemOptions.find((systemOption) => systemOption.value === Number(this.initialSystemId));
        if (initialSystemOption !== undefined) {
          this.selectedSystem = initialSystemOption;
          return;
        }

        if (this.initialSystemId === SystemSelectionType.DYNAMIC) {
          [this.selectedSystem] = this.systemOptions;
          return;
        }

        this.selectedSystem = null;
      },
      preselectChart() {
        const charts = flatMap(this.chartOptions, (chartOption) => chartOption.charts);
        this.selectedChart = charts.find((chart) => chart.key === this.initialChartKey) ?? charts[0];
      },
      preselectPeriod() {
        this.selectedPeriod = this.periodOptions.find((periodOption) => periodOption.value === this.initialPeriod)
          ?? this.periodOptions[0];
      },
      async updateSystemOptions() {
        const unsortedSystems = await Base.getSystems();
        this.systemOptions = Base.sortSystems(unsortedSystems)
          .map((system) => ({ value: system.id, label: system.label }));
      },
      async onChange() {
        await this.prepareChartSelection();
        this.preparePeriodSelection();
      },
      async prepareChartSelection() {
        if (this.systemType === SystemSelectionType.SYSTEM && this.selectedSystem === null) {
          this.selectedChart = null;
          this.chartOptions = [];
          return;
        }
        const systemId = this.systemType !== SystemSelectionType.DYNAMIC ? this.selectedSystem.value : null;
        this.isLoadingCharts = true;
        this.chartOptions = await ChartStaticService.getAvailableCharts(this.isOnSystemLevel ? VcomProvider.systemId : systemId);
        this.isLoadingCharts = false;

        const chartKeys = flatMap(this.chartOptions, (chartOption) => chartOption.charts.map((chart) => chart.key));

        if (this.selectedChart === null || !chartKeys.includes(this.selectedChart?.key)) {
          [this.selectedChart] = this.chartOptions[0].charts;
        }
      },
      preparePeriodSelection() {
        if (this.systemType === SystemSelectionType.SYSTEM && this.selectedSystem === null) {
          this.selectedPeriod = null;
          this.periodOptions = [];
          return;
        }
        const { periods } = this.selectedChart;

        this.periodOptions = periods.map((period) => ({
          label: this.tr(periodLabelMapping[period]),
          value: period,
        }));

        if (this.selectedPeriod === null || !periods.includes(this.selectedPeriod?.value)) {
          [this.selectedPeriod] = this.periodOptions;
        }

        this.isPeriodSelectionDisabled = periods.length === 1;
      },
    },
  };
</script>
